import { useEffect, useState } from 'react'
import MuiBox from '@mui/material/Box'
import { useFormik, Form, FormikProvider } from 'formik'
import * as Yup from 'yup'
import FormikControl from '../../../components/formik-control'
import { useGetFieldsQuery, usePostFieldMutation } from '../../../services/fields/slice'

const FieldForm = ({ currentField, onSubmitEvent, setSubmitEvent, setShowDrawer, fixedEntity }) => {
  const { data: fields } = useGetFieldsQuery('fields')
  const [postField] = usePostFieldMutation()
  const [entityOptions, setEntityOptions] = useState([])
  const [typeOptions, setTypeOptions] = useState([])
  const [referenceEntities, setReferenceEntities] = useState([])

  useEffect(() => {
    if (fields) {
      const options = fields.find((field) => field.name === 'entity')?.options || []
      setEntityOptions(options)
      const typeOptions = fields.find((field) => field.name === 'type')?.options || []
      setTypeOptions(typeOptions)
      const referenceEntities = [
        { key: 'Client', value: 'client' },
        { key: 'User', value: 'user' },
        { key: 'Campaign', value: 'campaign' },
      ]
      setReferenceEntities(referenceEntities)
    }
  }, [fields])

  const initialValues = {
    id: currentField?.id || '',
    name: currentField?.name || '',
    label: currentField?.label || '',
    description: currentField?.description || '',
    entity: fixedEntity || currentField?.entity || '',
    type: currentField?.type || '',
    options: currentField?.options || '',
    referenceEntity: currentField?.referenceEntity || '',
    width: currentField?.width || 200,
    priority: currentField?.priority || 100,
    isReadOnly: currentField?.isReadOnly || false,
    isAdminOnly: currentField?.isAdminOnly || false,
    isSystemOnly: currentField?.isSystemOnly || false,
  }

  const validationSchema = Yup.object({
    name: Yup.string().required('Required'),
    label: Yup.string().required('Required'),
    description: Yup.string(),
    entity: Yup.string().required('Required'),
    type: Yup.string().required('Required'),
    width: Yup.number(),
    priority: Yup.number(),
    options: Yup.string().when('type', {
      is: 'enum',
      then: () => Yup.string().required('Options are required for this type'),
    }),
    referenceEntity: Yup.string().when('type', {
      is: 'reference',
      then: () => Yup.string().required('The entity is required for this type'),
    }),
  })

  const onSubmit = async (values) => {
    const valuesToPost = {
      ...values,
      id: values.id ?? undefined,
      options: values.options?.trim() ? values.options : undefined,
      referenceEntity: values.referenceEntity?.trim() ? values.referenceEntity : undefined,
      width: values.width ? parseInt(values.width) : undefined,
      priority: values.priority ? parseInt(values.priority) : undefined,
    }

    await postField(valuesToPost)
    setSubmitEvent()
    setShowDrawer(false)
  }

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit,
  })

  useEffect(() => {
    if (onSubmitEvent) {
      formik.handleSubmit()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onSubmitEvent])

  return (
    <>
      {entityOptions?.length > 0 && (
        <FormikProvider value={formik}>
          <Form>
            <MuiBox>
              <FormikControl control="input" label="Name" name="name" fullWidth margin="normal" />
            </MuiBox>
            <MuiBox>
              <FormikControl control="input" label="Label" name="label" fullWidth margin="normal" />
            </MuiBox>
            <MuiBox>
              <FormikControl control="input" label="Description" name="description" fullWidth margin="normal" />
            </MuiBox>
            <MuiBox>
              <FormikControl
                control="select"
                label="Entity"
                name="entity"
                fullWidth
                disabled={!!fixedEntity}
                options={entityOptions}
              />
            </MuiBox>
            <MuiBox>
              <FormikControl control="input" label="Priority" name="priority" />
            </MuiBox>
            <MuiBox>
              <FormikControl control="input" label="Width" name="width" />
            </MuiBox>
            <MuiBox>
              <FormikControl control="select" label="Type" name="type" fullWidth options={typeOptions} />
            </MuiBox>
            {formik.values.type === 'enum' && (
              <MuiBox sx={{ ml: 3 }}>
                <FormikControl control="input" label="Selection Options" name="options" fullWidth />
              </MuiBox>
            )}
            {formik.values.type === 'reference' && (
              <MuiBox sx={{ ml: 3 }}>
                <FormikControl
                  control="select"
                  label="Reference Entity"
                  name="referenceEntity"
                  fullWidth
                  options={referenceEntities}
                />
              </MuiBox>
            )}
            <MuiBox>
              <FormikControl control="checkbox" label="Read Only" name="isReadOnly" />
            </MuiBox>
            <MuiBox>
              <FormikControl control="checkbox" label="Admin Only" name="isAdminOnly" />
            </MuiBox>
            <MuiBox>
              <FormikControl control="checkbox" label="System Only" name="isSystemOnly" />
            </MuiBox>
          </Form>
        </FormikProvider>
      )}
    </>
  )
}

export default FieldForm
